/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import DOMPurify from "dompurify";
import { FC, useState } from "react";

import Button from "web/Components/Common/ButtonCC";
import Dialog from "web/Components/mui/Dialog";

import benefitsBannerImg from "web/assets/images/benefits-banner-v4.png";

import useIsOverflowing from "web/hooks/useIsOverflowing";

import setBenefitGroupTypeParam from "web/utils/page/product/subscription/setBenefitGroupTypeParam";

import urls from "web/constants/urls";

import { Nullable } from "web/types/Utils";

import { useAppContext } from "web/context/app";

import __ from "../Translations";
import classes from "./subscriptionBanner.scss";

interface ISubscriptionBannerProps {
  finalDates: Nullable<{ fullDateFromDate: string; fullDateToDate: string }>;
  hasBothGroupsEnabled?: boolean;
  isGroupOneAvailable?: boolean;
  isGroupTwoAvailable?: boolean;
  bannerContent?: Nullable<string>;
  bannerContentUMS?: Nullable<string>;
}

const SubscriptionBanner: FC<ISubscriptionBannerProps> = ({
  finalDates,
  hasBothGroupsEnabled,
  bannerContent,
  bannerContentUMS,
  isGroupOneAvailable,
  isGroupTwoAvailable,
}) => {
  const { isMobile } = useAppContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { textRef, isOverflowing } = useIsOverflowing();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Paper
      sx={{
        bgcolor: { xs: "grey.100", md: "white" },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 2 },
        borderRadius: { xs: "0", md: "48px" },
        overflow: "hidden",
      }}
      variant={isMd ? "outlined" : "elevation"}
      elevation={0}
    >
      {/* info */}
      <Box
        sx={{
          width: { xs: "auto", md: "50%" },
          pt: { xs: 4, md: 5.25 },
          pb: { xs: 0, md: 5.25 },
          px: { xs: 2, md: 11, lg: 15, xl: 17 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* dates */}
        <Box
          sx={{
            marginBottom: { xs: 2, md: 3 },
          }}
        >
          {finalDates &&
            (hasBothGroupsEnabled ? (
              <>
                <Typography
                  variant="h2"
                  color="primary.dark"
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                    marginBottom: 0.5,
                  }}
                >
                  {__("Sprawdź do kiedy")}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  {__("możesz wybrać abonamenty.")}
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                    marginBottom: 0.5,
                  }}
                >
                  {__("Wyboru abonamentów dokonaj")}
                </Typography>
                <Typography
                  variant="h2"
                  color="primary.dark"
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                    marginBottom: 0.5,
                  }}
                >
                  {__("do")}&nbsp;{finalDates.fullDateToDate}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                    marginBottom: 0.5,
                  }}
                >
                  {__("aby zostać nimi objętym")}
                </Typography>
                <Typography
                  variant="h2"
                  color="primary.dark"
                  sx={{ textAlign: { xs: "center", md: "left" } }}
                >
                  {__("od")}&nbsp;{finalDates.fullDateFromDate}.
                </Typography>
              </>
            ))}
        </Box>

        {/* banner content & dialog */}
        <Box
          sx={{
            mb: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: { xs: "center", md: "left" },
              marginBottom: { xs: 0, md: 1 },
              lineClamp: 5,
              display: "-webkit-box",
              WebkitLineClamp: 5,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            ref={textRef}
          >
            {isGroupOneAvailable && bannerContent && (
              <Box
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerContent),
                }}
              />
            )}
            {isGroupTwoAvailable && bannerContentUMS && (
              <Box
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerContentUMS),
                }}
              />
            )}
          </Typography>
          {/* Following elements are not from MUI */}
          {isOverflowing && (
            <button
              type="button"
              className={classes.readMore}
              onClick={() => setDialogOpen(true)}
            >
              {__("Czytaj całość")}
            </button>
          )}
        </Box>
        <Dialog
          fullScreen={isMobile}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={__("Informacje dotyczące wyboru abonamentów")}
        >
          <Typography variant="body2">
            {isGroupOneAvailable && bannerContent && (
              <Box
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerContent),
                }}
              />
            )}
            {isGroupTwoAvailable && bannerContentUMS && (
              <Box
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerContentUMS),
                }}
              />
            )}
          </Typography>
        </Dialog>

        {/* buttons - these are not from MUI */}
        <Box
          sx={{
            marginTop: "auto",
            marginBottom: { xs: 3, md: 0 },
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 1, lg: 2 },
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          {!hasBothGroupsEnabled && (
            <Button
              to={`${
                urls.checkoutSubscriptionConfiguration
              }${setBenefitGroupTypeParam(isGroupTwoAvailable ? 2 : 1)}`}
            >
              {__("Zarządzaj abonamentami")}
            </Button>
          )}

          <Button to={urls.customerSubscriptions} variant="secondary">
            {__("Moje abonamenty")}
          </Button>
        </Box>
      </Box>

      {/* image */}
      <Box
        component="img"
        sx={{ width: { xs: "auto", md: "50%" }, objectFit: "cover" }}
        alt="Abonamenty"
        src={benefitsBannerImg}
      />
    </Paper>
  );
};

export default SubscriptionBanner;
